
import React from "react";
import { Control, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Heart } from "lucide-react";
import { WaitlistFormValues, salutationOptions } from "../schema";

interface SectionProps {
  control: Control<WaitlistFormValues>;
}

export function ResidentDetailsSection({ control }: SectionProps) {
  // Watch the residentRelation field to conditionally render other fields
  const residentRelation = useWatch({
    control,
    name: "residentRelation",
    defaultValue: "parent", // Default is parent
  });

  // Only show salutation and last name if not "self"
  const showResidentDetails = residentRelation !== "self";

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-grandpal-beige">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Heart className="h-5 w-5 text-grandpal-green" />
        Resident Details
      </h3>
      
      <FormField
        control={control}
        name="residentRelation"
        render={({ field }) => (
          <FormItem className="space-y-3 mb-4">
            <FormLabel>Who is the nursing home for?</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                value={field.value}
                className="flex flex-col space-y-1"
              >
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="self" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Myself
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="parent" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Parent
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="spouse" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Spouse/Partner
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="other" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Other family member or friend
                  </FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {showResidentDetails && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="residentSalutation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Salutation</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select salutation" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {salutationOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="residentLastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Resident Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      
      <div className="mt-4">
        <FormField
          control={control}
          name="residentAge"
          render={({ field }) => (
            <FormItem className="max-w-xs">
              <FormLabel>Resident Age</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 75" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
